import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { votingApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Vote,
  Plus,
  ArrowLeft,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  BarChart3,
  Loader2,
  AlertCircle,
  FileText,
  Trash2,
  Ban,
  Check,
  X,
  Upload,
  Download,
  Eye,
  Shield,
  PlusCircle,
  MinusCircle,
  Printer,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VOTE_TYPES = [
  { id: 'yes_no', name: 'Yes / No / Unsure', defaultOptions: ['Yes', 'No', 'Unsure'] },
  { id: 'multiple_choice', name: 'Multiple Choice', defaultOptions: ['Option A', 'Option B', 'Option C'] },
  { id: 'election', name: 'Trustee Election', defaultOptions: [] },
];

export default function AdminVoting() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [votes, setVotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [selectedVote, setSelectedVote] = useState(null);
  const [voteReport, setVoteReport] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Form state
  const [voteForm, setVoteForm] = useState({
    title: '',
    description: '',
    vote_type: 'yes_no',
    options: ['Yes', 'No', 'Unsure'],
    candidates: [{ name: '', bio: '', email: '' }, { name: '', bio: '', email: '' }],
    quorum_required: 50,
    eligible_voters: 'all',
    start_date: '',
    end_date: '',
    attachments: [],
  });

  const hoaId = user?.hoa_id || 'demo_hoa';
  const token = localStorage.getItem('session_token');
  const isManagingAgent = ['managing_agent', 'agent_admin', 'platform_owner'].includes(user?.role);

  useEffect(() => {
    fetchVotes();
  }, [activeTab, hoaId]);

  const fetchVotes = async () => {
    setLoading(true);
    try {
      const status = activeTab === 'all' ? null : activeTab;
      const data = await votingApi.list(hoaId, status);
      setVotes(data || []);
      
      // Calculate stats
      const active = (data || []).filter(v => v.status === 'active').length;
      const closed = (data || []).filter(v => v.status === 'closed').length;
      const draft = (data || []).filter(v => v.status === 'draft').length;
      const totalVoters = (data || []).reduce((sum, v) => sum + (v.voters_count || 0), 0);
      setStats({ active, closed, draft, total: (data || []).length, totalVoters });
    } catch (error) {
      console.error('Error fetching votes:', error);
      toast.error('Failed to load votes');
      setVotes([]);
      setStats({ active: 0, closed: 0, draft: 0, total: 0, totalVoters: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVote = async () => {
    if (!voteForm.title || !voteForm.start_date || !voteForm.end_date) {
      toast.error('Title, start date, and end date are required');
      return;
    }

    if (voteForm.vote_type === 'election') {
      const validCandidates = voteForm.candidates.filter(c => c.name.trim() !== '');
      if (validCandidates.length < 2) {
        toast.error('Add at least 2 candidates for an election');
        return;
      }
    } else if (voteForm.options.length < 2) {
      toast.error('At least 2 options are required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        hoa_id: hoaId,
        title: voteForm.title,
        description: voteForm.description,
        vote_type: voteForm.vote_type,
        quorum_required: voteForm.quorum_required,
        eligible_voters: voteForm.eligible_voters,
        start_date: new Date(voteForm.start_date).toISOString(),
        end_date: new Date(voteForm.end_date).toISOString(),
        attachments: voteForm.attachments,
      };
      if (voteForm.vote_type === 'election') {
        payload.candidates = voteForm.candidates
          .filter(c => c.name.trim() !== '')
          .map(c => ({ name: c.name.trim(), bio: c.bio || '', email: (c.email || '').trim().toLowerCase() || null }));
        payload.options = payload.candidates.map(c => c.name);
      } else {
        payload.options = voteForm.options.filter(o => o.trim() !== '');
      }
      await votingApi.create(payload);

      toast.success('Vote created successfully! Notifications will be sent to residents.');
      setShowCreateDialog(false);
      resetForm();
      fetchVotes();
    } catch (error) {
      console.error('Error creating vote:', error);
      toast.error(error.message || 'Failed to create vote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseVote = async (voteId) => {
    if (!window.confirm('Are you sure you want to close this vote? This action cannot be undone.')) return;
    
    try {
      await votingApi.close(voteId);
      toast.success('Vote closed. Results are now final.');
      fetchVotes();
      setShowResultsDialog(false);
    } catch (error) {
      console.error('Error closing vote:', error);
      toast.error('Failed to close vote');
    }
  };

  const handleDeleteVote = async (voteId) => {
    if (!window.confirm('Are you sure you want to delete this vote? This action cannot be undone.')) return;

    try {
      await votingApi.delete(voteId);
      toast.success('Vote deleted');
      fetchVotes();
    } catch (error) {
      console.error('Error deleting vote:', error);
      toast.error(error.message || 'Failed to delete vote');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await votingApi.uploadAttachment(hoaId, formData);
      
      if (result.file_url) {
        setVoteForm(prev => ({
          ...prev,
          attachments: [...prev.attachments, {
            file_name: result.file_name,
            file_url: result.file_url,
            file_size: result.file_size,
          }]
        }));
        toast.success('File uploaded');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleViewReport = async (vote) => {
    setSelectedVote(vote);
    setShowReportDialog(true);
    
    try {
      const report = await votingApi.getReport(vote.vote_id);
      setVoteReport(report);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    }
  };

  const handleDownloadPdf = async (vote) => {
    try {
      const response = await votingApi.getPdfReport(vote.vote_id);
      
      // Create a new window with the HTML content for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(response.html);
      printWindow.document.close();
      
      // Auto print
      setTimeout(() => {
        printWindow.print();
      }, 500);
      
      toast.success('PDF report ready for download');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const resetForm = () => {
    setVoteForm({
      title: '',
      description: '',
      vote_type: 'yes_no',
      options: ['Yes', 'No', 'Unsure'],
      candidates: [{ name: '', bio: '', email: '' }, { name: '', bio: '', email: '' }],
      quorum_required: 50,
      eligible_voters: 'all',
      start_date: '',
      end_date: '',
      attachments: [],
    });
  };

  const addCandidate = () => {
    setVoteForm(prev => ({ ...prev, candidates: [...prev.candidates, { name: '', bio: '', email: '' }] }));
  };
  const removeCandidate = (index) => {
    if (voteForm.candidates.length <= 2) {
      toast.error('Minimum 2 candidates required');
      return;
    }
    setVoteForm(prev => ({ ...prev, candidates: prev.candidates.filter((_, i) => i !== index) }));
  };
  const updateCandidate = (index, field, value) => {
    setVoteForm(prev => ({
      ...prev,
      candidates: prev.candidates.map((c, i) => i === index ? { ...c, [field]: value } : c),
    }));
  };

  const handleVoteTypeChange = (type) => {
    const voteType = VOTE_TYPES.find(t => t.id === type);
    setVoteForm(prev => ({
      ...prev,
      vote_type: type,
      options: voteType?.defaultOptions || ['Option A', 'Option B'],
    }));
  };

  const addOption = () => {
    setVoteForm(prev => ({
      ...prev,
      options: [...prev.options, `Option ${prev.options.length + 1}`],
    }));
  };

  const removeOption = (index) => {
    if (voteForm.options.length <= 2) {
      toast.error('Minimum 2 options required');
      return;
    }
    setVoteForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const updateOption = (index, value) => {
    setVoteForm(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt),
    }));
  };

  const removeAttachment = (index) => {
    setVoteForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return '';
    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;
    const days = Math.ceil(diff / 86400000);
    if (days < 0) return 'Ended';
    if (days === 0) return 'Ends today';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  };

  const getQuorumProgress = (vote) => {
    return vote.total_units > 0 ? (vote.voters_count / vote.total_units) * 100 : 0;
  };

  const getWinningOption = (vote) => {
    if (!vote.results || Object.keys(vote.results).length === 0) return null;
    let maxVotes = 0;
    let winner = null;
    Object.entries(vote.results).forEach(([option, count]) => {
      if (count > maxVotes) {
        maxVotes = count;
        winner = option;
      }
    });
    return winner;
  };

  const filteredVotes = votes.filter(v => {
    if (activeTab === 'all') return true;
    return v.status === activeTab;
  });

  // Back navigation based on role
  const getBackPath = () => {
    if (user?.role === 'trustee') return '/trustee-hub';
    if (['managing_agent', 'agent_admin'].includes(user?.role)) return '/agent';
    if (user?.role === 'platform_owner') return '/platform-owner';
    return '/admin';
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="admin-voting-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2 text-slate-600" onClick={() => navigate(getBackPath())}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Voting & Resolutions</h1>
              <p className="text-slate-500 mt-1">Create and manage building votes and resolutions</p>
            </div>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700" 
              onClick={() => setShowCreateDialog(true)}
              data-testid="create-vote-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New Vote
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {!loading && stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Vote className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.active}</p>
                    <p className="text-sm text-slate-500">Active</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.draft}</p>
                    <p className="text-sm text-slate-500">Draft</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.closed}</p>
                    <p className="text-sm text-slate-500">Closed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total}</p>
                    <p className="text-sm text-slate-500">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.totalVoters}</p>
                    <p className="text-sm text-slate-500">Votes Cast</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="active" data-testid="tab-active">
              Active
              {stats?.active > 0 && (
                <Badge className="ml-2 bg-emerald-500">{stats.active}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="draft" data-testid="tab-draft">Draft</TabsTrigger>
            <TabsTrigger value="closed" data-testid="tab-closed">Closed</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-64 bg-slate-200 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : filteredVotes.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Vote className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No votes found</h3>
                  <p className="text-slate-500 mb-4">
                    {activeTab === 'active' ? 'No active votes at the moment' : 
                     activeTab === 'draft' ? 'No draft votes' : 'No voting history'}
                  </p>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Vote
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredVotes.map(vote => {
                  const winner = getWinningOption(vote);
                  const quorumMet = getQuorumProgress(vote) >= vote.quorum_required;
                  const totalVotes = Object.values(vote.results || {}).reduce((a, b) => a + b, 0);
                  
                  return (
                    <Card 
                      key={vote.vote_id}
                      className="hover:shadow-lg transition-shadow"
                      data-testid={`vote-card-${vote.vote_id}`}
                    >
                      <CardContent className="p-5">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex flex-wrap gap-1">
                            {vote.status === 'active' ? (
                              <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                            ) : vote.status === 'draft' ? (
                              <Badge className="bg-amber-100 text-amber-700">Draft</Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-600">Closed</Badge>
                            )}
                            <Badge variant="outline" className="text-xs capitalize">
                              {vote.vote_type?.replace('_', ' ')}
                            </Badge>
                          </div>
                          {vote.status !== 'closed' && (
                            <div className="flex gap-1">
                              {vote.status === 'active' && (
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  className="h-8 w-8 text-amber-600"
                                  onClick={() => handleCloseVote(vote.vote_id)}
                                  title="Close voting"
                                >
                                  <Ban className="w-4 h-4" />
                                </Button>
                              )}
                              {(vote.voters_count || 0) === 0 && (
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  className="h-8 w-8 text-red-600"
                                  onClick={() => handleDeleteVote(vote.vote_id)}
                                  title="Delete vote"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">{vote.title}</h3>
                        <p className="text-sm text-slate-500 mb-4 line-clamp-2">{vote.description}</p>

                        {/* Results Preview */}
                        {totalVotes > 0 && (
                          <div className="space-y-2 mb-4">
                            {(vote.options || []).slice(0, 3).map(option => {
                              const count = vote.results?.[option] || 0;
                              const pct = totalVotes > 0 ? (count / totalVotes) * 100 : 0;
                              const isWinner = option === winner;
                              
                              return (
                                <div key={option} className="space-y-1">
                                  <div className="flex justify-between text-xs">
                                    <span className={isWinner ? 'font-semibold text-emerald-600' : 'text-slate-600'}>
                                      {option} {isWinner && vote.status === 'closed' && <CheckCircle2 className="w-3 h-3 inline" />}
                                    </span>
                                    <span className="text-slate-500">{count} ({pct.toFixed(0)}%)</span>
                                  </div>
                                  <Progress 
                                    value={pct} 
                                    className={`h-1.5 ${isWinner ? '[&>div]:bg-emerald-500' : ''}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Stats */}
                        <div className="pt-3 border-t space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500 flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              Participation
                            </span>
                            <span className="font-medium">
                              {vote.voters_count || 0}/{vote.total_units || 0} ({Math.round(getQuorumProgress(vote))}%)
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">Quorum ({vote.quorum_required}%)</span>
                            <Badge className={quorumMet ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {quorumMet ? 'Met' : 'Not Met'}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {vote.status === 'active' ? 'Deadline' : vote.status === 'draft' ? 'Starts' : 'Ended'}
                            </span>
                            <span className={vote.status === 'active' ? 'text-amber-600 font-medium' : 'text-slate-600'}>
                              {vote.status === 'draft' ? formatDate(vote.start_date) : formatDate(vote.end_date)}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 mt-4">
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => {
                              setSelectedVote(vote);
                              setShowResultsDialog(true);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </Button>
                          {isManagingAgent && vote.status === 'closed' && (
                            <Button 
                              variant="outline"
                              onClick={() => handleViewReport(vote)}
                            >
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Vote Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Vote className="w-5 h-5 text-indigo-600" />
                Create New Vote
              </DialogTitle>
              <DialogDescription>
                Create a resolution for residents to vote on. Votes are anonymous and secure.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-5 py-4">
              {/* Title */}
              <div>
                <Label>Vote Title *</Label>
                <Input
                  value={voteForm.title}
                  onChange={(e) => setVoteForm({...voteForm, title: e.target.value})}
                  placeholder="e.g., Approval of Garden Renovation Project"
                  className="mt-1"
                  data-testid="vote-title-input"
                />
              </div>

              {/* Description */}
              <div>
                <Label>Description / Details</Label>
                <Textarea
                  value={voteForm.description}
                  onChange={(e) => setVoteForm({...voteForm, description: e.target.value})}
                  placeholder="Provide details about what residents are voting on..."
                  className="mt-1"
                  rows={3}
                  data-testid="vote-description-input"
                />
              </div>

              {/* Vote Type */}
              <div>
                <Label>Vote Type</Label>
                <Select 
                  value={voteForm.vote_type} 
                  onValueChange={handleVoteTypeChange}
                >
                  <SelectTrigger className="mt-1" data-testid="vote-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VOTE_TYPES.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Options / Candidates */}
              {voteForm.vote_type === 'election' ? (
                <div data-testid="election-candidates-section">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Candidates</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={addCandidate} className="text-indigo-600" data-testid="add-candidate-btn">
                      <PlusCircle className="w-4 h-4 mr-1" /> Add Candidate
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {voteForm.candidates.map((cand, index) => (
                      <div key={index} className="p-3 border rounded-lg space-y-2 bg-slate-50">
                        <div className="flex gap-2">
                          <Input
                            value={cand.name}
                            onChange={(e) => updateCandidate(index, 'name', e.target.value)}
                            placeholder={`Candidate ${index + 1} name`}
                            data-testid={`candidate-name-${index}`}
                          />
                          {voteForm.candidates.length > 2 && (
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeCandidate(index)} className="text-red-500 hover:text-red-600" data-testid={`remove-candidate-${index}`}>
                              <MinusCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <Input
                          type="email"
                          value={cand.email}
                          onChange={(e) => updateCandidate(index, 'email', e.target.value)}
                          placeholder="Candidate email (optional, enables auto-promotion on win)"
                          data-testid={`candidate-email-${index}`}
                        />
                        <Textarea
                          value={cand.bio}
                          onChange={(e) => updateCandidate(index, 'bio', e.target.value)}
                          placeholder="Short bio (optional) — experience, vision, why they want to serve"
                          rows={2}
                          data-testid={`candidate-bio-${index}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Voting Options</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={addOption}
                      className="text-indigo-600"
                    >
                      <PlusCircle className="w-4 h-4 mr-1" />
                      Add Option
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {voteForm.options.map((option, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={option}
                          onChange={(e) => updateOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          data-testid={`vote-option-${index}`}
                        />
                        {voteForm.options.length > 2 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeOption(index)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <MinusCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={voteForm.start_date}
                    onChange={(e) => setVoteForm({...voteForm, start_date: e.target.value})}
                    className="mt-1"
                    data-testid="vote-start-date"
                  />
                </div>
                <div>
                  <Label>End Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={voteForm.end_date}
                    onChange={(e) => setVoteForm({...voteForm, end_date: e.target.value})}
                    className="mt-1"
                    data-testid="vote-end-date"
                  />
                </div>
              </div>

              {/* Quorum & Eligibility */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quorum Required (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={voteForm.quorum_required}
                    onChange={(e) => setVoteForm({...voteForm, quorum_required: parseInt(e.target.value) || 50})}
                    className="mt-1"
                    data-testid="vote-quorum-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Minimum participation % for valid result</p>
                </div>
                <div>
                  <Label>Eligible Voters</Label>
                  <Select 
                    value={voteForm.eligible_voters} 
                    onValueChange={(v) => setVoteForm({...voteForm, eligible_voters: v})}
                  >
                    <SelectTrigger className="mt-1" data-testid="vote-eligibility-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Residents</SelectItem>
                      <SelectItem value="owners_only">Owners Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Attachments */}
              <div>
                <Label>Attachments (Optional)</Label>
                <p className="text-xs text-slate-500 mb-2">Add supporting documents like quotes, budgets, or proposals</p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                  className="w-full"
                >
                  {uploadingFile ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload File
                </Button>

                {voteForm.attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {voteForm.attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-sm truncate max-w-[200px]">{att.file_name}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => removeAttachment(idx)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Security Info */}
              <div className="p-3 bg-emerald-50 rounded-lg flex items-start gap-2">
                <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-emerald-700">Secure & Anonymous Voting</p>
                  <p className="text-xs text-emerald-600">
                    One vote per unit. Voter identities are anonymized. Only managing agents can see full audit data.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateVote} 
                disabled={submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="submit-create-vote"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Vote'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Results Dialog */}
        <Dialog open={showResultsDialog} onOpenChange={setShowResultsDialog}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedVote?.title}</DialogTitle>
            </DialogHeader>
            {selectedVote && (
              <div className="space-y-4 py-4">
                <div className="flex gap-2 flex-wrap">
                  {selectedVote.status === 'active' ? (
                    <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                  ) : selectedVote.status === 'draft' ? (
                    <Badge className="bg-amber-100 text-amber-700">Draft</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600">Closed</Badge>
                  )}
                  <Badge variant="outline" className="capitalize">{selectedVote.vote_type?.replace('_', ' ')}</Badge>
                  <Badge variant="outline">{selectedVote.eligible_voters === 'owners_only' ? 'Owners Only' : 'All Residents'}</Badge>
                </div>
                
                <p className="text-sm text-slate-600">{selectedVote.description}</p>

                {/* Election Winner Banner */}
                {selectedVote.vote_type === 'election' && selectedVote.winner && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="election-winner-banner">
                    <p className="text-sm font-medium text-emerald-800">
                      🏆 Winner: <strong>{selectedVote.winner}</strong>
                    </p>
                  </div>
                )}
                {selectedVote.vote_type === 'election' && selectedVote.is_tie && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="election-tie-banner">
                    <p className="text-sm font-medium text-amber-800">
                      Tie between: {(selectedVote.tied_candidates || []).join(', ')}
                    </p>
                  </div>
                )}

                {/* Attachments */}
                {selectedVote.attachments && selectedVote.attachments.length > 0 && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium text-slate-700 mb-2">Attachments</p>
                    {selectedVote.attachments.map((att, idx) => (
                      <a 
                        key={idx}
                        href={`${API_URL}${att.file_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        <FileText className="w-4 h-4" />
                        {att.file_name}
                      </a>
                    ))}
                  </div>
                )}

                {/* Results */}
                <div className="space-y-3">
                  <Label>Results</Label>
                  {(selectedVote.options || []).map(option => {
                    const count = selectedVote.results?.[option] || 0;
                    const total = Object.values(selectedVote.results || {}).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? (count / total) * 100 : 0;
                    const isWinner = option === getWinningOption(selectedVote);
                    
                    return (
                      <div key={option} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className={isWinner ? 'font-semibold text-emerald-600' : 'text-slate-700'}>
                            {option} {isWinner && selectedVote.status === 'closed' && <CheckCircle2 className="w-4 h-4 inline" />}
                          </span>
                          <span className="text-slate-500">{count} votes ({pct.toFixed(1)}%)</span>
                        </div>
                        <Progress 
                          value={pct} 
                          className={`h-2 ${isWinner ? '[&>div]:bg-emerald-500' : ''}`}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* Stats */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Total Votes</span>
                    <span className="font-medium">{Object.values(selectedVote.results || {}).reduce((a, b) => a + b, 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Participation</span>
                    <span className="font-medium">{selectedVote.voters_count || 0}/{selectedVote.total_units || 0} ({Math.round(getQuorumProgress(selectedVote))}%)</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Quorum ({selectedVote.quorum_required}%)</span>
                    <Badge className={getQuorumProgress(selectedVote) >= selectedVote.quorum_required ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {getQuorumProgress(selectedVote) >= selectedVote.quorum_required ? 'Met' : 'Not Met'}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Voting Period</span>
                    <span className="text-slate-700">{formatDate(selectedVote.start_date)} - {formatDate(selectedVote.end_date)}</span>
                  </div>
                </div>

                {/* Outcome */}
                {selectedVote.status === 'closed' && (
                  <div className={`p-4 rounded-lg ${getQuorumProgress(selectedVote) >= selectedVote.quorum_required ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                    <div className="flex items-center gap-2">
                      {getQuorumProgress(selectedVote) >= selectedVote.quorum_required ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      )}
                      <div>
                        <p className={`font-semibold ${getQuorumProgress(selectedVote) >= selectedVote.quorum_required ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {getQuorumProgress(selectedVote) >= selectedVote.quorum_required ? 'Resolution Passed' : 'Quorum Not Reached'}
                        </p>
                        <p className={`text-sm ${getQuorumProgress(selectedVote) >= selectedVote.quorum_required ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {getQuorumProgress(selectedVote) >= selectedVote.quorum_required 
                            ? `"${getWinningOption(selectedVote)}" won with majority`
                            : 'Result may not be binding'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setShowResultsDialog(false)}>Close</Button>
              {selectedVote?.status === 'active' && (
                <Button 
                  variant="destructive"
                  onClick={() => handleCloseVote(selectedVote.vote_id)}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Close Voting
                </Button>
              )}
              {isManagingAgent && selectedVote?.status === 'closed' && (
                <Button onClick={() => handleDownloadPdf(selectedVote)}>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Report Dialog (Managing Agents Only) */}
        <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                Vote Report
              </DialogTitle>
            </DialogHeader>
            {voteReport ? (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <h3 className="font-semibold text-indigo-900">{voteReport.title}</h3>
                  <p className="text-sm text-indigo-700">{voteReport.building_name}</p>
                </div>

                {/* Participation Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-indigo-600">{voteReport.participation_rate}%</p>
                      <p className="text-sm text-slate-500">Participation Rate</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-indigo-600">{voteReport.units_voted}/{voteReport.total_eligible_units}</p>
                      <p className="text-sm text-slate-500">Units Voted</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Results Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Results Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(voteReport.results || {}).map(([option, count]) => (
                        <div key={option} className="flex items-center justify-between">
                          <span className={option === voteReport.winning_option ? 'font-semibold text-emerald-600' : 'text-slate-700'}>
                            {option}
                            {option === voteReport.winning_option && <CheckCircle2 className="w-4 h-4 inline ml-2" />}
                          </span>
                          <span className="text-slate-600">
                            {count} votes ({voteReport.percentages?.[option]}%)
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Quorum Status */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Quorum Status</p>
                        <p className="text-sm text-slate-500">Required: {voteReport.quorum_required}%</p>
                      </div>
                      <Badge className={voteReport.quorum_met ? 'bg-emerald-100 text-emerald-700 text-lg px-4 py-1' : 'bg-amber-100 text-amber-700 text-lg px-4 py-1'}>
                        {voteReport.quorum_met ? '✓ Quorum Met' : '✗ Quorum Not Met'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Timeline */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-2">Voting Period</p>
                  <p className="font-medium">{formatDate(voteReport.start_date)} - {formatDate(voteReport.end_date)}</p>
                  <p className="text-xs text-slate-400 mt-2">Created by {voteReport.created_by} on {formatDate(voteReport.created_at)}</p>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
                <p className="text-slate-500 mt-2">Loading report...</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowReportDialog(false); setVoteReport(null); }}>
                Close
              </Button>
              {selectedVote && (
                <Button onClick={() => handleDownloadPdf(selectedVote)}>
                  <Printer className="w-4 h-4 mr-2" />
                  Print / Save PDF
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
